import { messages as ptMessages } from './pt-BR';
import { messages as enMessages } from './en';

/**
 * No i18n library. Two locales, no right-to-left, no date arithmetic, and a
 * key set known at compile time — `Intl.PluralRules` and `Intl.NumberFormat`
 * (see `./format`) cover the whole requirement. The cost, stated: a third
 * locale or ICU message syntax replaces this rather than extending it.
 *
 * pt-BR is the default; `en` is the fallback locale, not the default one.
 */
export type Locale = 'pt-BR' | 'en';

/** The key set for the shared foundation's own catalogue: the shell, the auth flow, and §6.6's
 *  four normative statements — see `pt-BR.ts` for why the landing page is
 *  not among them. `pt-BR.ts` is the source of truth for this union; `en.ts`
 *  is checked against it both at the type level (its `Record<keyof typeof
 *  ptMessages, string>` annotation) and at the value level
 *  (`src/i18n/__tests__/i18n.test.ts`). */
export type MessageKey = keyof typeof import('./pt-BR').messages;

/** Placeholder syntax used by every catalogue's `t`: `{name}` is replaced by
 *  `vars.name`, stringified as-is. Number formatting is deliberately not done
 *  here — anything that needs locale-aware grouping or rounding goes through
 *  `./format` and is interpolated as an already-formatted string. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

/** CLDR puts pt's 0 in `one` ("0 campanha"); Brazilian usage, and the
 *  product's copy, count nothing in the plural ("0 campanhas"). */
export function pluralCategory(count: number, locale: Locale): Intl.LDMLPluralRule {
  return count === 0 ? 'other' : new Intl.PluralRules(locale).select(count);
}

/** `Intl.PluralRules` category naming convention for a pluralisable key: the
 *  key passed to `plural()` is always the `.other` form (the one guaranteed
 *  to exist), and a locale-specific category — `one`, `few`, `many`, `two`,
 *  `zero` — is tried first by substituting it for the trailing `.other`. Both
 *  pt-BR and en only ever produce `one` or `other`, but the substitution is
 *  generic so a catalogue is free to add the others. */
function pluralKeyFor(key: string, count: number, locale: Locale): string {
  const category = pluralCategory(count, locale);
  if (category === 'other' || !key.endsWith('.other')) return key;
  const stem = key.slice(0, -'.other'.length);
  return `${stem}.${category}`;
}

/** Where a locale set explicitly (today: only `ops`'s boot-time pin) is
 *  remembered, mirroring `THEME_STORAGE_KEY` in `../tokens/theme`. */
export const LOCALE_STORAGE_KEY = 'socialsrc.locale';

function isLocale(value: string): value is Locale {
  return value === 'pt-BR' || value === 'en';
}

/** Storage throws rather than returning null in a browser configured to
 *  block site data, and an unreadable or invalid preference falls back to
 *  the default locale rather than failing the module to load. */
function storedLocale(): Locale {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return raw !== null && isLocale(raw) ? raw : 'pt-BR';
  } catch {
    return 'pt-BR';
  }
}

let currentLocale: Locale = storedLocale();

/** Read anywhere; every catalogue — the global one and every
 *  `defineCatalogue` catalogue — reads this same module-level value, so
 *  `setLocale` re-points all of them at once. */
export function getLocale(): Locale {
  return currentLocale;
}

/**
 * `persist: false` is for `src/ops/main.tsx`'s boot-time pin only: ops and
 * the tenant app share one browser origin and therefore one `localStorage`,
 * so a persisting call there would overwrite whatever a future tenant-side
 * language selector had stored, on every ops page load. Every other caller
 * — a future selector included — persists, which is the whole point: its
 * entire implementation is "call `setLocale()`".
 */
export function setLocale(locale: Locale, options?: { persist?: boolean }): void {
  currentLocale = locale;
  if (options?.persist === false) return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Not worth failing the call over; the in-memory locale above still took.
  }
}

/** A feature area's own strings, with its own typed key union and its own
 *  `t`. `Record<keyof T, string>` on the second argument is what makes a
 *  missing English string a compile error rather than a runtime hole. */
export interface Catalogue<T extends Record<string, string>> {
  t: (key: keyof T, vars?: Record<string, string | number>) => string;
  plural: (key: keyof T, count: number, vars?: Record<string, string | number>) => string;
  keys: (keyof T)[];
}

/**
 * Build a per-area catalogue. The control plane, the ops plane and tenant
 * self-serve each call this once, in their own `messages.ts`, and
 * never touch this file or `pt-BR.ts` — the same collision the route glob
 * exists to prevent, avoided here by construction: nobody's key set lives in
 * a file another plan also has to modify.
 *
 * The catalogue returned observes the same active locale as the global one:
 * `t` and `plural` below read `getLocale()` on every call rather than closing
 * over the locale at construction time, so `setLocale('en')` re-points every
 * catalogue built this way — and the global one — in the same instant.
 */
export function defineCatalogue<const T extends Record<string, string>>(
  pt: T,
  en: Record<keyof T, string>,
): Catalogue<T> {
  const store: Record<Locale, Record<keyof T, string>> = { 'pt-BR': pt, en };
  // Falls back to the English entry if the active locale is somehow missing
  // the key (it should not be — see the type-and-test guarantee above — but a
  // lookup that cannot fail is safer than one that renders the raw key to a
  // tenant), and to the raw key as a last resort so a genuinely absent string
  // is visible rather than silently blank.
  const lookup = (locale: Locale, key: keyof T): string =>
    store[locale][key] ?? store.en[key] ?? String(key);
  return {
    t: (key, vars) => interpolate(lookup(getLocale(), key), vars),
    plural: (key, count, vars) => {
      const locale = getLocale();
      const resolvedKey = pluralKeyFor(String(key), count, locale) as keyof T;
      return interpolate(lookup(locale, resolvedKey), vars);
    },
    keys: Object.keys(pt) as (keyof T)[],
  };
}

const globalCatalogue = defineCatalogue(ptMessages, enMessages);

/**
 * Every key in the global catalogue.
 *
 * `Catalogue.keys` exists so a catalogue can be audited rather than read, and
 * for a while nothing called it -- which is how sixteen keys came to sit in
 * this catalogue referenced by nothing at all, three of them permanently
 * unreachable. `__tests__/i18n.test.ts` now sweeps production
 * sources against this list.
 */
export const messageKeys: MessageKey[] = globalCatalogue.keys;

export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  return globalCatalogue.t(key, vars);
}

export function plural(
  key: MessageKey,
  count: number,
  vars?: Record<string, string | number>,
): string {
  return globalCatalogue.plural(key, count, vars);
}
